'use strict';

import { DEVICE_POSITION_OPTIONS } from './devicePosition';

/**
 * Per-trial research metadata ("tags") collected on the Record page.
 *
 * Field keys are the researchers' spreadsheet column names and travel to the
 * backend unchanged (see BACKEND_TRIAL_TAGS_MIGRATION.md). Most fields ship with
 * no preset option list: the vocabulary is the researchers' to define and it
 * grows mid-study, so those values are free text typed into a creatable input.
 * Device, left position, and right position are not typed. After each trial the
 * recorder downloads that trial's device files, and those filenames fill the
 * three columns (see deviceTagsFromDownloadedFiles).
 *
 * "none" is a real answer meaning the clinician confirmed there was no value
 * for that field. Empty means nobody filled the field in. The two must not be
 * collapsed into each other when reading or writing the API — payloads keep
 * an explicit "none". Chip summaries under the patient name / Recordings Tags
 * column omit placeholder-like answers ("none", "n/a", "null", …) so only
 * informative values show (see isMeaningfulTagValue).
 *
 * `sub` is not listed here: the subject is the patient chosen in Select Patient,
 * so it is derived at save time rather than retyped (see buildTrialTagPayload).
 */

/** Answers that mean "no useful label" and must not appear as chips. */
const NON_DISPLAY_TAG_VALUES = new Set([
    'none',
    'null',
    'nil',
    'undefined',
    'na',
    'n/a',
    'n.a.',
    'n.a',
    'not applicable',
    'not available',
    '-',
    '--',
    '—',
    '–',
]);

export const SESSION_SCOPE = 'session';
export const TRIAL_SCOPE = 'trial';

/** Field descriptors in display order. `group` drives the layout headings. */
export const TRIAL_TAG_FIELDS = [
    { key: 'cond', label: 'Condition', scope: SESSION_SCOPE, group: 'Participant', type: 'select' },
    { key: 'lab', label: 'Lab', scope: SESSION_SCOPE, group: 'Setup', type: 'select' },
    // derived: filled from the device files downloaded after the trial, not typed.
    { key: 'device', label: 'Device', scope: TRIAL_SCOPE, group: 'Devices', type: 'select', derived: true },
    { key: 'leftpos', label: 'Left device', scope: TRIAL_SCOPE, group: 'Devices', type: 'select', options: DEVICE_POSITION_OPTIONS, derived: true },
    { key: 'rightpos', label: 'Right device', scope: TRIAL_SCOPE, group: 'Devices', type: 'select', options: DEVICE_POSITION_OPTIONS, derived: true },
    { key: 'action', label: 'Action', scope: TRIAL_SCOPE, group: 'Protocol', type: 'select' },
    { key: 'walkingform', label: 'Walking form', scope: TRIAL_SCOPE, group: 'Protocol', type: 'select' },
    { key: 'aidtype', label: 'Aid type', scope: TRIAL_SCOPE, group: 'Protocol', type: 'select' },
    { key: 'speed', label: 'Speed', scope: TRIAL_SCOPE, group: 'Protocol', type: 'select' },
    { key: 'comments', label: 'Comments', scope: TRIAL_SCOPE, group: 'Notes', type: 'text' },
    { key: 'tags', label: 'Tags', scope: TRIAL_SCOPE, group: 'Notes', type: 'tags' },
];

export function fieldsForScope(scope) {
    return TRIAL_TAG_FIELDS.filter((field) => field.scope === scope);
}

/** Fields of a scope bucketed into `[{ group, fields }]`, keeping descriptor order. */
export function groupsForScope(scope) {
    const groups = [];
    fieldsForScope(scope).forEach((field) => {
        if (field.derived) return;
        const existing = groups.find((group) => group.group === field.group);
        if (existing) {
            existing.fields.push(field);
        } else {
            groups.push({ group: field.group, fields: [field] });
        }
    });
    return groups;
}

/** Preset choices first, then remembered values, deduped case-insensitively. */
export function mergeFieldOptions(preset, remembered) {
    const seen = new Set();
    const values = [];
    [preset, remembered].forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((value) => {
            if (typeof value !== 'string') return;
            const trimmed = value.trim();
            const key = trimmed.toLowerCase();
            if (!key || seen.has(key)) return;
            seen.add(key);
            values.push(trimmed);
        });
    });
    return values;
}

function emptyValue(field) {
    return field.type === 'tags' ? [] : '';
}

/** Blank value map for a scope, so inputs are always controlled. */
export function defaultTagValues(scope) {
    const values = {};
    fieldsForScope(scope).forEach((field) => {
        values[field.key] = emptyValue(field);
    });
    return values;
}

/**
 * Trial-scoped values for a new trial. Selected values carry over so only the
 * fields that changed need re-entering. Free-form tags and device tags start
 * empty; device tags are filled from that trial's downloaded files.
 */
export function inheritTrialTags(previousTags) {
    const values = defaultTagValues(TRIAL_SCOPE);
    if (!previousTags) return values;
    fieldsForScope(TRIAL_SCOPE).forEach((field) => {
        if (field.type === 'tags' || field.derived) return;
        const previous = previousTags[field.key];
        if (previous === undefined || previous === null) return;
        values[field.key] = Array.isArray(previous) ? previous.slice() : previous;
    });
    return values;
}

function pad(value) {
    return String(value).padStart(2, '0');
}

/** Trial label in the researchers' format, e.g. `2019-05-09--12-08-06`. */
export function formatTrialTimestamp(date) {
    const when = date instanceof Date && !isNaN(date.getTime()) ? date : new Date();
    return [
        `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`,
        `${pad(when.getHours())}-${pad(when.getMinutes())}-${pad(when.getSeconds())}`,
    ].join('--');
}

export function buildSessionName(date) {
    return `session-${formatTrialTimestamp(date)}`;
}

function hasValue(value) {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
}

/**
 * True when a tag value is worth showing as a chip. Blanks and placeholder
 * strings (none / n/a / null / …) are omitted from summaries even though
 * "none" remains a valid stored answer.
 */
export function isMeaningfulTagValue(value) {
    if (Array.isArray(value)) return value.some(isMeaningfulTagValue);
    if (!hasValue(value)) return false;
    if (typeof value !== 'string') return true;
    const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalized) return false;
    return !NON_DISPLAY_TAG_VALUES.has(normalized);
}

/**
 * Set values of a scope as chip descriptors for the collapsed summaries.
 * Multi-value `tags` contribute one chip per entry. Placeholder answers are
 * skipped so the bar under the patient name only shows informative tags.
 */
export function summarizeTags(values, scope) {
    const chips = [];
    if (!values) return chips;
    fieldsForScope(scope).forEach((field) => {
        const value = values[field.key];
        if (!isMeaningfulTagValue(value)) return;
        if (Array.isArray(value)) {
            value.forEach((entry, index) => {
                if (isMeaningfulTagValue(entry)) {
                    chips.push({ key: `${field.key}-${index}`, label: field.label, value: entry });
                }
            });
            return;
        }
        chips.push({ key: field.key, label: field.label, value });
    });
    return chips;
}

function readTagValue(field, raw) {
    if (raw === undefined || raw === null) return emptyValue(field);
    if (field.type === 'tags') return Array.isArray(raw) ? raw.slice() : [];
    return raw;
}

/** Maps a trial row from the API into the session/trial tag value maps the UI edits. */
export function parseTrialTagsFromApi(trial) {
    const sessionTags = defaultTagValues(SESSION_SCOPE);
    const trialTags = defaultTagValues(TRIAL_SCOPE);
    if (!trial) return { sessionTags, trialTags };
    fieldsForScope(SESSION_SCOPE).forEach((field) => {
        sessionTags[field.key] = readTagValue(field, trial[field.key]);
    });
    fieldsForScope(TRIAL_SCOPE).forEach((field) => {
        trialTags[field.key] = readTagValue(field, trial[field.key]);
    });
    return { sessionTags, trialTags };
}

/** Chip list for every set tag on a trial, session fields first then trial fields. */
export function summarizeAllTags(sessionTags, trialTags) {
    return [
        ...summarizeTags(sessionTags, SESSION_SCOPE),
        ...summarizeTags(trialTags, TRIAL_SCOPE),
    ];
}

/**
 * Compact chip set for the Recordings table Tags column: walking form, aid type
 * (historical; no longer collected on Record), and free-form tags — only when
 * present on the trial row.
 */
export const RECORDINGS_TABLE_TAG_KEYS = ['walkingform', 'aidtype', 'tags'];

/** Labels for Tags-column keys, including historical columns left off the editors. */
const RECORDINGS_TABLE_TAG_LABELS = {
    walkingform: 'Walking form',
    aidtype: 'Aid type',
    tags: 'Tags',
};

export function summarizeRecordingsTableTags(trial) {
    if (!trial) return [];
    const chips = [];
    RECORDINGS_TABLE_TAG_KEYS.forEach((key) => {
        const value = trial[key];
        const label = RECORDINGS_TABLE_TAG_LABELS[key];
        if (!isMeaningfulTagValue(value)) return;
        if (Array.isArray(value)) {
            value.forEach((entry, index) => {
                if (!isMeaningfulTagValue(entry)) return;
                chips.push({ key: `${key}-${index}`, label, value: entry });
            });
            return;
        }
        chips.push({ key, label, value });
    });
    return chips;
}

/**
 * Wire payload for `POST /a/update-trial`.
 *
 * Every field the Record page owns is sent on each save, with blanks as
 * explicit `null`, so clearing a field actually clears it in the database.
 * Fields outside this set (date, notes) are never touched.
 *
 * `sub`, `trial`, and `session` are derived rather than typed: the subject is
 * the selected patient, and the two identifiers come from the recording clock.
 */
export function buildTrialTagPayload({ sessionTags, trialTags, subject, trialKey, sessionKey } = {}) {
    const payload = {};
    TRIAL_TAG_FIELDS.forEach((field) => {
        const source = field.scope === SESSION_SCOPE ? sessionTags : trialTags;
        const value = source ? source[field.key] : undefined;
        if (field.type === 'tags') {
            payload[field.key] = Array.isArray(value) ? value.filter(hasValue) : [];
            return;
        }
        payload[field.key] = hasValue(value) ? value : null;
    });
    payload.sub = hasValue(subject) ? subject : null;
    payload.trial = hasValue(trialKey) ? trialKey : null;
    payload.session = hasValue(sessionKey) ? sessionKey : null;
    return payload;
}
